/**
 * API Service for backend communication
 * Handles all HTTP requests to the backend server
 */

import axios from 'axios';

// Get API URL from environment or use default
// In production, if no API URL is set, use relative URL (same domain)
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`Making ${config.method?.toUpperCase()} request to: ${config.url}`);
    }
    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('Request setup error');
    }
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`Response from ${response.config.url}:`, response.data);
    }
    return response;
  },
  (error) => {
    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      // Log safely - don't expose sensitive data
      if (process.env.NODE_ENV === 'development') {
        console.error(`API Error [${error.response.status}] ${error.config?.url}:`, {
          status: error.response.status,
          message: error.response.data?.detail || error.response.data?.message || 'Server error'
        });
      } else {
        // In production, only log minimal info
        console.error(`API Error: ${error.response.status}`);
      }

      // Extract meaningful error message
      const errorMessage =
        error.response.data?.detail ||
        error.response.data?.message ||
        'An error occurred';

      return Promise.reject({
        status: error.response.status,
        message: errorMessage,
        data: error.response.data
      });
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error: Unable to reach server');
      return Promise.reject({
        status: 0,
        message: 'Unable to connect to server. Please check your connection.',
        isNetworkError: true
      });
    } else {
      // Something else happened
      console.error('Request setup error');
      return Promise.reject({
        status: 0,
        message: error.message || 'An unexpected error occurred'
      });
    }
  }
);

/**
 * Waitlist API functions
 */
export const waitlistApi = {
  /**
   * Subscribe email to waitlist
   * @param {string} email - Email address to subscribe
   * @param {string} [name] - Optional subscriber name
   * @param {string} [source] - Source of subscription (default: 'website')
   * @returns {Promise} API response with subscription status
   */
  subscribe: async (email, name = null, source = 'website') => {
    try {
      const response = await api.post('/api/waitlist/subscribe', {
        email,
        name,
        source
      });
      return response.data;
    } catch (error) {
      // Re-throw with formatted error
      throw {
        success: false,
        message: error.message || 'Failed to subscribe',
        error: error.data?.error || error.message,
        ...error
      };
    }
  },

  /**
   * Update user profile with survey responses
   * @param {string} email - User's email address
   * @param {Object} profileData - Survey responses and other profile data
   * @returns {Promise} API response with update status
   */
  updateProfile: async (email, profileData) => {
    try {
      const response = await api.post('/api/waitlist/update-profile', {
        email,
        ...profileData
      });
      return response.data;
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to update profile',
        error: error.data?.error || error.message,
        status: error.status
      };
    }
  },

  /**
   * Check if email is already subscribed
   * @param {string} email - Email address to check
   * @returns {Promise} Subscription status
   */
  checkSubscription: async (email) => {
    try {
      const response = await api.get(`/api/waitlist/check/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Don't throw, return false for check failures
      return { subscribed: false };
    }
  },

  /**
   * Update individual survey step data
   * @param {string} email - User's email address
   * @param {number} step - Step number (1-4)
   * @param {Object} data - Step data to save
   * @param {boolean} completed - Whether the step was completed
   * @param {boolean} skipped - Whether the step was skipped
   * @param {string} [profileToken] - Per-email token issued by subscribe(), required for authorization
   * @returns {Promise} API response with save status
   */
  updateStep: async (email, step, data, completed = false, skipped = false, profileToken = null) => {
    try {
      const response = await api.post('/api/waitlist/update-step', {
        email,
        step,
        data,
        completed,
        skipped
      }, profileToken ? { headers: { 'X-Profile-Token': profileToken } } : undefined);
      return response.data;
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to save progress',
        error: error.data?.error || error.message,
        status: error.status
      };
    }
  },

  /**
   * Get waitlist statistics (admin only)
   * @returns {Promise} Waitlist stats
   */
  getStats: async () => {
    try {
      const response = await api.get('/api/waitlist/stats');
      return response.data;
    } catch (error) {
      throw {
        success: false,
        message: 'Failed to fetch statistics',
        error: error.message,
        status: error.status
      };
    }
  }
};

// Export the api instance for other potential uses
export default api;